import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Compass, ExternalLink } from "lucide-react";

/* Small reusable row used inside InsightsPanel. */
function InsightRow({ label, children, empty, cta, onCta, testid }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-start p-2.5"
         style={{ background: "rgba(255,255,255,0.85)", border: "2px solid #0F1B2D" }}
         data-testid={testid}>
      <div className="md:col-span-4 text-[12px] leading-snug"
           style={{ color: "#0F1B2D", fontWeight: 600 }}>
        {label}
      </div>
      <div className="md:col-span-8">
        {children || (
          <div>
            <div className="text-[12px] leading-snug mb-1.5" style={{ color: "#475569" }}>{empty}</div>
            {cta && onCta && (
              <button onClick={onCta} className="text-[11px] font-semibold rm-text-orange inline-flex items-center gap-1 hover:underline">
                {cta} <ArrowRight size={11} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Personalized Insights — Skills worth · Top jobs · Adjacent roles.
 * Renders an aware empty state per row depending on whether the user
 * has a resume + a skills assessment + matching live jobs.
 */
export default function InsightsPanel({ hasResume, skillsWorth, topJobs, adjacentRoles, roleHint }) {
  const navigate = useNavigate();
  const fmt = (n, c) => n ? `${c} ${Math.round(n).toLocaleString()}` : "—";

  // ROW 1 — Skills worth
  const SkillsRow = () => {
    if (!hasResume) {
      return (
        <InsightRow testid="insight-skills-worth"
          label="Your Skills Are Worth…"
          empty="Add your resume, then run a Skill Assessment."
          cta="Add resume" onCta={() => navigate("/feed-resume")} />
      );
    }
    if (!skillsWorth) {
      return (
        <InsightRow testid="insight-skills-worth"
          label="Your Skills Are Worth…"
          empty="Run a Skill Assessment to see your salary range."
          cta="Run assessment" onCta={() => navigate("/skills/new")} />
      );
    }
    return (
      <InsightRow testid="insight-skills-worth"
        label="Your Skills Are Worth…"
        onCta={() => navigate(`/skills/${skillsWorth.assessment_id}`)}
        cta="See assessment →">
        <div className="font-display leading-none"
             style={{
               fontSize: "clamp(1.15rem, 2.1vw, 1.5rem)",
               background: "linear-gradient(90deg, #FF6A00 0%, #8C3A00 100%)",
               WebkitBackgroundClip: "text",
               WebkitTextFillColor: "transparent",
               backgroundClip: "text",
               color: "#FF6A00",
               letterSpacing: "-0.02em",
             }}
             data-testid="insight-aim-point">
          {fmt(skillsWorth.mid, skillsWorth.currency)}
        </div>
        <div className="text-[11px] mt-1 leading-snug" style={{ color: "#0F1B2D", fontWeight: 500 }}>
          Range {fmt(skillsWorth.low, skillsWorth.currency)} — {fmt(skillsWorth.high, skillsWorth.currency)}
          {skillsWorth.role && <> · {skillsWorth.role}</>}
        </div>
      </InsightRow>
    );
  };

  // ROW 1.5 — Level-up projection (only when current_salary + level_up_projection present)
  const LevelUpRow = () => {
    const proj = skillsWorth?.level_up_projection;
    if (!proj || !proj.items?.length) return null;
    return (
      <InsightRow testid="insight-level-up"
        label="What if I Leveled Up?"
        onCta={() => navigate(`/skills/${skillsWorth.assessment_id}`)}
        cta="See full plan →">
        <div className="font-display leading-none"
             style={{
               fontSize: "clamp(1rem, 1.9vw, 1.3rem)",
               color: "#065F46",
               letterSpacing: "-0.02em",
             }}
             data-testid="insight-level-up-total">
          +{fmt(proj.total_uplift_usd, skillsWorth.currency)}
        </div>
        <div className="text-[11px] mt-1 leading-snug" style={{ color: "#0F1B2D", fontWeight: 500 }}>
          From {fmt(proj.from_salary, skillsWorth.currency)} → {fmt(proj.to_salary, skillsWorth.currency)}
          {proj.capped && (
            <span className="font-mono-rm text-[9px] uppercase tracking-widest ml-1.5 opacity-70">
              capped at high band
            </span>
          )}
        </div>
        <div className="mt-2 space-y-1">
          {proj.items.slice(0, 3).map((it, i) => (
            <div key={i}
                 className="flex items-baseline gap-2 text-[11px] leading-snug px-2 py-1 rounded"
                 style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.4)" }}
                 data-testid={`insight-level-up-item-${i}`}>
              <span className="font-mono-rm text-[10px] font-bold rm-text-orange whitespace-nowrap">
                +{fmt(it.uplift_usd, skillsWorth.currency)}
              </span>
              <span style={{ color: "#0F1B2D", fontWeight: 600 }}>{it.skill}</span>
              {it.premium === "high" && (
                <span className="font-mono-rm text-[9px] uppercase tracking-widest px-1 rounded"
                      style={{ background: "#10B981", color: "white" }}>
                  high premium
                </span>
              )}
            </div>
          ))}
        </div>
      </InsightRow>
    );
  };

  // ROW 2 — Top job opportunities
  const JobsRow = () => {
    if (!hasResume) {
      return (
        <InsightRow testid="insight-top-jobs"
          label="Top Job Opportunities for Your Role"
          empty="Add your resume so Resmo can match live openings."
          cta="Add resume" onCta={() => navigate("/feed-resume")} />
      );
    }
    if (!topJobs || topJobs.length === 0) {
      return (
        <InsightRow testid="insight-top-jobs"
          label="Top Job Opportunities for Your Role"
          empty={roleHint
            ? `No live matches found for "${roleHint}" right now. Browse Job Search.`
            : "Run a Skill Assessment so we know what role to match."}
          cta={roleHint ? "Browse Job Search" : "Run assessment"}
          onCta={() => navigate(roleHint ? "/job-search" : "/skills/new")} />
      );
    }
    return (
      <InsightRow testid="insight-top-jobs"
        label={`Top 3 Job Opportunities${roleHint ? ` · ${roleHint}` : ""}`}
        onCta={() => navigate("/job-search")}
        cta="See all →">
        <ul className="space-y-1.5">
          {topJobs.slice(0, 3).map((j, i) => {
            const hasDescription = (j.description || "").trim().length >= 50;
            const handleTailor = (e) => {
              e.stopPropagation();
              if (!hasDescription) return;
              navigate("/feed-job", {
                state: {
                  prefill: {
                    jobDescription: j.description || "",
                    jobTitle: j.title || "",
                    companyName: j.company || "",
                    source: "adzuna",
                    adzuna_url: j.url || "",
                  },
                },
              });
            };
            return (
              <li
                key={j.id || i}
                className="flex items-start gap-2 group cursor-pointer p-1.5 rounded transition-colors hover:bg-white/40"
                onClick={handleTailor}
                role="button"
                tabIndex={hasDescription ? 0 : -1}
                onKeyDown={(e) => { if (hasDescription && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleTailor(e); } }}
                data-testid={`insight-job-${i}`}
                title={hasDescription ? "Click to tailor your resume to this job" : "Open at source"}
              >
                <Building2 size={12} className="flex-shrink-0 mt-1" style={{ color: "#0F1B2D" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold leading-snug truncate group-hover:underline" style={{ color: "#0F1B2D" }}>
                    {j.title}
                  </div>
                  <div className="text-[11px] leading-snug" style={{ color: "#475569" }}>
                    {j.company || "—"}{j.location ? ` · ${j.location}` : ""}
                    {(j.salary_min || j.salary_max) && (
                      <> · ${Math.round((j.salary_min || j.salary_max) / 1000)}k{j.salary_max && j.salary_min ? `–${Math.round(j.salary_max / 1000)}k` : ""}</>
                    )}
                  </div>
                  {hasDescription && (
                    <div
                      className="font-mono-rm text-[9px] uppercase tracking-widest mt-0.5 inline-flex items-center gap-1"
                      style={{ color: "#B91C1C", fontWeight: 700 }}
                      data-testid={`insight-job-${i}-tailor-hint`}
                    >
                      Tailor my resume → <ArrowRight size={9} />
                    </div>
                  )}
                </div>
                {j.url && (
                  <a
                    href={j.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 mt-1"
                    style={{ color: "#FF6A00" }}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`insight-job-${i}-external`}
                    title="Open at source"
                  >
                    <ExternalLink size={12} />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </InsightRow>
    );
  };

  // ROW 3 — Adjacent roles
  const RolesRow = () => {
    if (!hasResume || !skillsWorth) {
      return (
        <InsightRow testid="insight-adjacent-roles"
          label="Other Job Titles That Fit Your Skills"
          empty="Run a Skill Assessment to see roles your skills also unlock."
          cta="Run assessment" onCta={() => navigate("/skills/new")} />
      );
    }
    if (!adjacentRoles || adjacentRoles.length === 0) {
      return (
        <InsightRow testid="insight-adjacent-roles"
          label="Other Job Titles That Fit Your Skills"
          empty="No adjacent roles identified in your latest assessment."
          cta="Re-run" onCta={() => navigate("/skills/new")} />
      );
    }
    return (
      <InsightRow testid="insight-adjacent-roles"
        label="Other Job Titles That Fit Your Skills"
        onCta={() => skillsWorth?.assessment_id && navigate(`/skills/${skillsWorth.assessment_id}`)}
        cta="See all →">
        <ul className="space-y-1.5">
          {adjacentRoles.slice(0, 3).map((a, i) => {
            const fit = Math.max(0, Math.min(100, Number(a.fit_pct) || 0));
            const fitColor = fit >= 75 ? "#20C1A7" : fit >= 55 ? "#FFC145" : "#94A3B8";
            return (
              <li key={i} className="flex items-center gap-2" data-testid={`insight-adjacent-${i}`}>
                <Compass size={12} className="flex-shrink-0" style={{ color: "#0F1B2D" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold leading-snug truncate" style={{ color: "#0F1B2D" }}>
                    {a.title}
                  </div>
                  {a.salary_low && (
                    <div className="text-[11px]" style={{ color: "#475569" }}>
                      ${Math.round(a.salary_low / 1000)}k–${Math.round((a.salary_high || a.salary_low) / 1000)}k
                      {a.demand && <> · {a.demand}</>}
                    </div>
                  )}
                </div>
                <span className="font-display text-xs px-1.5 py-0.5 flex-shrink-0"
                      style={{ background: fitColor, color: "white", border: "2px solid #0F1B2D" }}>
                  {fit}%
                </span>
              </li>
            );
          })}
        </ul>
      </InsightRow>
    );
  };

  return (
    <div
      className="rm-card-static p-4 md:p-5"
      style={{
        background: "linear-gradient(180deg, #FFE49A 0%, #E8AF31 100%)",
        borderColor: "#0F1B2D",
        boxShadow: "5px 5px 0 0 #0F1B2D",
      }}
      data-testid="insights-panel"
    >
      <div className="font-display uppercase leading-none mb-3"
           style={{
             fontSize: "clamp(1.3rem, 2.6vw, 1.8rem)",
             color: "#0F1B2D",
             letterSpacing: "-0.02em",
           }}>
        Personalized Insights
      </div>
      <div className="space-y-2.5">
        <SkillsRow />
        <LevelUpRow />
        <JobsRow />
        <RolesRow />
      </div>
    </div>
  );
}
